// SSE client for POST /api/hero/search.
//
// Faithfully ports the pump() semantics from the legacy monolith
// (static/index.html, audit §6.1) into a typed, testable class. Behavior
// guarantees:
//
//   - SSE frames in the format `event: name\ndata: json\n\n`
//   - TCP-fragment safety: a partial line at chunk boundary is buffered
//     until the next read; `evtName` persists across reads
//   - Live event handlers fire as events arrive (for thinking_start /
//     thinking_progress); ALL events accumulate into the returned SSEData
//     map so the caller (T12 root) can inspect them after the stream closes
//   - Pre-SSE 4xx (e.g. validation errors): read body as text, parse as JSON,
//     synthesize an `oob` event, RESOLVE the promise (do not reject) — this
//     lets the UI render a friendly bubble instead of staying stuck on
//     "Scanning..." forever
//   - 429 → RateLimitError (caller can show distinct rate-limit message)
//   - Network failure → NetworkError
//   - 401/403 → AuthError (T12 falls back to anonymous mode + emits event)
//
// Uses fetch + ReadableStream (NOT EventSource — EventSource only supports
// GET; we need POST with JSON body).

import type { SearchRequest, SSEData, SSEEventName } from '../types/index.js';
import { AuthError, NetworkError, ParseError, RateLimitError } from './errors.js';

/**
 * Per-event live handlers. Optional — caller registers only the events it
 * cares about during streaming. All events still accumulate into the
 * returned SSEData map regardless of handler registration.
 */
export interface SSEHandlers {
  onThinkingStart?: (e: SSEData['thinking_start']) => void;
  onThinkingProgress?: (e: SSEData['thinking_progress']) => void;
  onThinkingComplete?: (e: SSEData['thinking_complete']) => void;
  onResult?: (e: SSEData['result']) => void;
  onFallback?: (e: SSEData['fallback']) => void;
  onError?: (e: SSEData['error']) => void;
  onConsultative?: (e: SSEData['consultative']) => void;
  onOOB?: (e: SSEData['oob']) => void;
  onDeployIntent?: (e: SSEData['deploy_intent']) => void;
  onPickResult?: (e: SSEData['pick_result']) => void;
  /** Generic catch-all fired for every event type. Useful for logging. */
  onAny?: (name: SSEEventName, payload: unknown) => void;
}

export interface SSEClientOptions {
  /** Base URL for the BFF. Empty string = same-origin (default). */
  apiBase?: string;
  /** Optional Bearer token. Settable post-construction via setAuthToken. */
  authToken?: string;
  /** Optional AbortSignal for early cancellation (e.g. on widget unmount). */
  signal?: AbortSignal;
  /** Override fetch — useful for tests. Defaults to globalThis.fetch. */
  fetchImpl?: typeof fetch;
}

export class SSEClient {
  private apiBase: string;
  private authToken: string | undefined;
  private fetchImpl: typeof fetch;

  constructor(opts: SSEClientOptions = {}) {
    this.apiBase = opts.apiBase ?? '';
    this.authToken = opts.authToken;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  setAuthToken(token: string | undefined): void {
    this.authToken = token;
  }

  /**
   * Run a search query and stream events through `handlers`. Returns the
   * accumulated SSEData map once the stream closes.
   *
   * Throws:
   *   - RateLimitError on 429 BEFORE any SSE frame opens
   *   - AuthError on 401 / 403 BEFORE any SSE frame opens
   *   - NetworkError on other fetch failures
   *
   * Does NOT throw on 4xx where the BFF returned a JSON error body — those
   * are translated into a synthetic `oob` event and the promise resolves.
   */
  async search(req: SearchRequest, handlers: SSEHandlers = {}, signal?: AbortSignal): Promise<SSEData> {
    const url = `${this.apiBase}/api/hero/search`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(req),
        signal,
      });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    if (!response.ok) {
      // Pre-SSE error path. Try to recover a friendly oob payload from
      // the JSON error body so the UI doesn't hang on "Scanning...".
      const bodyText = await response.text().catch(() => '');
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const seconds = retryAfter ? parseInt(retryAfter, 10) : undefined;
        throw new RateLimitError(
          'Too many requests. Try again in a moment.',
          bodyText,
          Number.isFinite(seconds) ? seconds : undefined,
        );
      }
      if (response.status === 401 || response.status === 403) {
        throw new AuthError(
          response.status === 401 ? 'Unauthorized' : 'Forbidden',
          response.status,
          bodyText,
        );
      }
      // 400 / 500 / etc. — synthesize oob event so UI shows a bubble.
      const oobMessage = parseOOBFromErrorBody(bodyText);
      const sseData: SSEData = {
        oob: { message: oobMessage, suggestions: [], portalCta: undefined },
      };
      if (handlers.onOOB) handlers.onOOB(sseData.oob);
      if (handlers.onAny) handlers.onAny('oob', sseData.oob);
      return sseData;
    }

    // Success path — stream and parse SSE frames.
    if (!response.body) {
      throw new NetworkError('response has no body to stream');
    }
    return parseSSEStream(response.body, handlers);
  }
}

/**
 * Read an SSE byte stream and parse `event:` / `data:` frames into typed
 * payloads. Faithfully ports the monolith's pump() loop with one difference:
 * uses async iteration over the reader for a cleaner control flow.
 *
 * Exposed as a separate function (not a method) so unit tests can construct
 * a mock ReadableStream and feed it directly without the full fetch path.
 */
export async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  handlers: SSEHandlers,
): Promise<SSEData> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const sseData: SSEData = {};

  let buf = '';
  let evtName = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // Split into lines; the trailing partial line (if any) goes back into buf.
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

        if (line.startsWith('event: ')) {
          evtName = line.slice(7).trim();
        } else if (line.startsWith('data: ') && evtName) {
          const json = line.slice(6);
          try {
            const parsed = JSON.parse(json);
            applyEvent(sseData, handlers, evtName as SSEEventName, parsed);
          } catch (e) {
            // Malformed JSON — log and drop the frame; do not abort the stream.
            // This mirrors the monolith's catch-and-continue semantics.
            // Surfacing as ParseError lets callers (or future logging
            // middleware) inspect what failed.
            void new ParseError(`failed to parse SSE data line: ${(e as Error).message}`, json);
          }
        } else if (line === '') {
          evtName = '';
        }
        // Other lines (comments, unknown fields) are silently ignored.
      }
    }
  } finally {
    // Final partial buffer, if any, may contain a complete-but-unflushed
    // event terminated by EOF. SSE spec says incomplete events at EOF
    // are discarded — match that.
    reader.releaseLock();
  }

  return sseData;
}

function applyEvent(
  sseData: SSEData,
  handlers: SSEHandlers,
  name: SSEEventName,
  payload: unknown,
): void {
  // Type assertion: the BFF is the source of truth for payload shape per
  // each event name. We trust its conformance to types.go shapes (verified
  // at runtime by integration tests, T16). At the parser layer, we type
  // the slot but don't validate field-by-field — overhead vs. yield is poor.
  switch (name) {
    case 'thinking_start':
      sseData.thinking_start = payload as SSEData['thinking_start'];
      handlers.onThinkingStart?.(sseData.thinking_start);
      break;
    case 'thinking_progress':
      sseData.thinking_progress = payload as SSEData['thinking_progress'];
      handlers.onThinkingProgress?.(sseData.thinking_progress);
      break;
    case 'thinking_complete':
      sseData.thinking_complete = payload as SSEData['thinking_complete'];
      handlers.onThinkingComplete?.(sseData.thinking_complete);
      break;
    case 'result':
      sseData.result = payload as SSEData['result'];
      handlers.onResult?.(sseData.result);
      break;
    case 'fallback':
      // Multiple fallback events may fire — last-write-wins per audit §6.3.
      sseData.fallback = payload as SSEData['fallback'];
      handlers.onFallback?.(sseData.fallback);
      break;
    case 'error':
      sseData.error = payload as SSEData['error'];
      handlers.onError?.(sseData.error);
      break;
    case 'consultative':
      sseData.consultative = payload as SSEData['consultative'];
      handlers.onConsultative?.(sseData.consultative);
      break;
    case 'oob':
      sseData.oob = payload as SSEData['oob'];
      handlers.onOOB?.(sseData.oob);
      break;
    case 'deploy_intent':
      sseData.deploy_intent = payload as SSEData['deploy_intent'];
      handlers.onDeployIntent?.(sseData.deploy_intent);
      break;
    case 'pick_result':
      sseData.pick_result = payload as SSEData['pick_result'];
      handlers.onPickResult?.(sseData.pick_result);
      break;
    default: {
      // Unknown event name — silently ignore. The BFF may add new events
      // we don't yet understand; the widget shouldn't crash on them.
      const _exhaustive: never = name;
      void _exhaustive;
      return;
    }
  }
  handlers.onAny?.(name, payload);
}

/**
 * Best-effort recovery of a user-friendly message from a BFF JSON error
 * body. The BFF returns `{"error": "..."}` for validation failures
 * (handler_validate.go); we prefer that string. Otherwise fall back to
 * a generic message.
 */
function parseOOBFromErrorBody(body: string): string {
  if (!body) return "Couldn't process that. Try rephrasing.";
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed === 'object' && parsed && typeof (parsed as { error?: unknown }).error === 'string') {
      return (parsed as { error: string }).error;
    }
  } catch {
    // Body wasn't JSON — fall through to default.
  }
  return "Couldn't process that. Try rephrasing.";
}
